import { FC, useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { trailsService } from '@/services/trails.service';
import { videoService } from '@/services/video.service';
import YouTubePlayer from '@/components/YouTubePlayer';
import VideoPlayer from '@/components/VideoPlayer';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useToast } from '@/contexts/ToastContext';
import type { LessonContent, TrailDetailAPI, LessonFSMStatus } from '@/types/trailDetail';

interface LessonSidebarItem {
  id: string;
  title: string;
  order: number;
  lesson_status: LessonFSMStatus;
}

const CourseLessonPage: FC = () => {
  const { trailId, lessonId } = useParams<{ trailId: string; lessonId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [lesson, setLesson] = useState<LessonContent | null>(null);
  const [trail, setTrail] = useState<TrailDetailAPI | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [videoPercent, setVideoPercent] = useState(0);
  const [showTimeHint, setShowTimeHint] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoUrlRetried, setVideoUrlRetried] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);

  // Invisible timer — uses both ref (for API payload) and state (for reactivity)
  const timerRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start timer on mount
  useEffect(() => {
    timerRef.current = 0;
    setTimeElapsed(0);
    intervalRef.current = setInterval(() => {
      timerRef.current += 1;
      setTimeElapsed(timerRef.current);
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [lessonId]);

  // Load lesson content and trail data
  useEffect(() => {
    if (!trailId || !lessonId) return;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [lessonData, trailData] = await Promise.all([
          trailsService.getLessonContent(trailId, lessonId),
          trailsService.getTrailDetail(trailId),
        ]);
        setLesson(lessonData);
        setTrail(trailData);
      } catch {
        setError('Erro ao carregar a aula');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [trailId, lessonId]);

  // Show time hint when close to min_time
  useEffect(() => {
    if (!lesson || lesson.lesson_status === 'COMPLETED') return;
    const checkTime = setInterval(() => {
      const remaining = lesson.min_time_seconds - timerRef.current;
      if (remaining > 0 && remaining <= 10) {
        setShowTimeHint(true);
      } else {
        setShowTimeHint(false);
      }
    }, 1000);
    return () => clearInterval(checkTime);
  }, [lesson]);

  // Fetch video URL for own videos
  const hasOwnVideo = !!lesson?.content.video_blob_name;
  const hasYouTubeVideo = !!lesson?.content.youtube_url;
  const hasVideo = hasOwnVideo || hasYouTubeVideo;

  useEffect(() => {
    if (!lessonId || !hasOwnVideo) {
      setVideoUrl(null);
      return;
    }
    const fetchUrl = async () => {
      try {
        const result = await videoService.getVideoUrl(lessonId);
        setVideoUrl(result.url);
      } catch {
        setVideoUrl(null);
      }
    };
    fetchUrl();
  }, [lessonId, hasOwnVideo]);

  // Report video progress to backend (debounced)
  const progressReportRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleVideoProgress = useCallback((percent: number) => {
    setVideoPercent(percent);
    if (progressReportRef.current) clearTimeout(progressReportRef.current);
    if (lessonId) {
      progressReportRef.current = setTimeout(() => {
        videoService.reportProgress(lessonId, percent, timerRef.current).catch(() => {});
      }, 5000);
    }
  }, [lessonId]);

  // Handle video error (SAS expired) — retry once
  const handleVideoError = useCallback(async () => {
    if (!lessonId || videoUrlRetried) return;
    setVideoUrlRetried(true);
    try {
      const result = await videoService.getVideoUrl(lessonId);
      setVideoUrl(result.url);
    } catch {
      showToast('Erro ao carregar o vídeo. Recarregue a página.', 'error');
    }
  }, [lessonId, videoUrlRetried, showToast]);

  // FSM-based: use lesson_status from backend
  const lessonStatus: LessonFSMStatus = lesson?.lesson_status || 'NOT_STARTED';
  const isCompleted = lessonStatus === 'COMPLETED';
  const isQuizPending = lessonStatus === 'QUIZ_PENDING';

  // UX feedback only — real validation is on the backend
  const timeSatisfied = lesson ? (isCompleted || timeElapsed >= lesson.min_time_seconds) : false;
  const videoSatisfied = lesson ? (isCompleted || !hasVideo || videoPercent >= 80) : false;
  const canAdvance = timeSatisfied && videoSatisfied;

  const handleAdvance = async () => {
    if (!trailId || !lessonId || !lesson) return;

    // If already completed, navigate directly
    if (isCompleted) {
      if (lesson.navigation.next) {
        navigate(`/trilhas/${trailId}/aula/${lesson.navigation.next.id}`);
      } else {
        navigate(`/trilhas/${trailId}`);
      }
      return;
    }

    // If already in QUIZ_PENDING, redirect to quiz
    if (isQuizPending) {
      navigate(`/trilhas/${trailId}/aula/${lessonId}/quiz`);
      return;
    }

    setIsCompleting(true);
    try {
      const result = await trailsService.completeLesson(trailId, lessonId, {
        time_spent: timerRef.current,
        video_watched_percent: hasVideo ? videoPercent : 0,
      });

      // Backend decides the action
      switch (result.action) {
        case 'show_quiz':
          navigate(`/trilhas/${trailId}/aula/${lessonId}/quiz`);
          break;
        case 'next_lesson':
          // Re-fetch trail to update sidebar before navigating
          try {
            const updatedTrail = await trailsService.getTrailDetail(trailId);
            setTrail(updatedTrail);
          } catch { /* proceed with navigation anyway */ }
          if (result.next_lesson_id) {
            navigate(`/trilhas/${trailId}/aula/${result.next_lesson_id}`);
          } else {
            navigate(`/trilhas/${trailId}`);
          }
          break;
        case 'trail_completed':
          navigate(`/certificado/${trailId}`);
          break;
      }

      window.dispatchEvent(new CustomEvent('notification:refresh'));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      const errorMsg = error?.response?.data?.error || 'Erro ao avançar. Tente novamente.';
      showToast(typeof errorMsg === 'string' ? errorMsg : 'Erro ao avançar. Tente novamente.', 'error');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleNavigateLesson = (targetId: string) => {
    if (!trailId || targetId === lessonId) return;
    navigate(`/trilhas/${trailId}/aula/${targetId}`);
  };

  // Build sidebar lessons
  const sidebarLessons: LessonSidebarItem[] = trail?.lessons
    .sort((a, b) => a.order - b.order)
    .map((l) => ({
      id: l.id,
      title: l.title,
      order: l.order,
      lesson_status: l.lesson_status || 'NOT_STARTED',
    })) || [];

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error || !lesson || !trail) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <div className="text-center">
          <p className="text-red-400 text-lg font-bold">Erro ao carregar aula</p>
          <p className="text-content-muted mt-2">{error}</p>
          <Link to={`/trilhas/${trailId || ''}`} className="text-accent mt-4 inline-block hover:underline">
            Voltar para a trilha
          </Link>
        </div>
      </div>
    );
  }

  const progressPercent = trail.xp_reward > 0
    ? Math.round((trail.xp_earned / trail.xp_reward) * 100)
    : 0;

  return (
    <div className="flex flex-col min-h-screen bg-surface-primary" data-testid="course-lesson-page">
      {/* XP Progress Bar */}
      <div className="bg-surface-card border-b border-theme-border px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Link to={`/trilhas/${trailId}`} className="text-content-muted hover:text-content-primary shrink-0">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium truncate">{trail.title}</p>
              <span className="text-xs text-accent font-bold shrink-0 ml-2">
                {trail.xp_earned} / {trail.xp_reward} XP
              </span>
            </div>
            <div className="h-2 bg-surface-tertiary rounded-full overflow-hidden">
              <div
                className="h-full bg-accent rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-8">
            {/* Own Video Player */}
            {hasOwnVideo && videoUrl && (
              <div className="mb-8">
                <VideoPlayer
                  src={videoUrl}
                  thumbnail={lesson.content.video_thumbnail_url}
                  title={lesson.content.video_title}
                  onProgressUpdate={handleVideoProgress}
                  onError={handleVideoError}
                />
              </div>
            )}

            {/* YouTube Player (fallback when no own video) */}
            {!hasOwnVideo && hasYouTubeVideo && lesson.content.youtube_url && (
              <div className="mb-8">
                <YouTubePlayer
                  videoUrl={lesson.content.youtube_url}
                  onProgressUpdate={handleVideoProgress}
                />
              </div>
            )}

            {/* Markdown Content */}
            <article className="prose prose-tema prose-xl max-w-none mb-8" data-testid="markdown-content">
              <h1 className="text-3xl font-bold mb-6">{lesson.title}</h1>
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                {lesson.content.markdown}
              </ReactMarkdown>
            </article>

            {/* Attachments */}
            {lesson.content.attachments.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-bold text-content-secondary uppercase tracking-wider mb-3">
                  Materiais de Apoio
                </h3>
                <div className="space-y-2">
                  {lesson.content.attachments.map((att, idx) => (
                    <a
                      key={idx}
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 bg-surface-card border border-theme-border rounded-xl px-4 py-3 hover:border-accent/40 transition-colors"
                    >
                      <span className="material-symbols-outlined text-accent">attach_file</span>
                      <span className="flex-1 truncate text-sm font-medium">{att.name}</span>
                      <span className="text-content-muted text-xs">
                        {(att.size_bytes / 1024).toFixed(0)} KB
                      </span>
                      <span className="material-symbols-outlined text-content-muted text-lg">download</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Time hint */}
            {showTimeHint && !isCompleted && (
              <p className="text-content-muted text-sm text-center mb-4">Leia com calma...</p>
            )}
          </div>
        </main>

        {/* Lesson Sidebar */}
        <aside className="w-72 border-l border-theme-border bg-surface-card hidden lg:flex flex-col overflow-y-auto" data-testid="lesson-sidebar">
          <div className="p-4 border-b border-theme-border">
            <p className="text-xs text-content-muted uppercase font-bold tracking-wider">Páginas</p>
            <p className="text-sm text-content-secondary mt-1">
              {lesson.navigation.current} de {lesson.navigation.total}
            </p>
          </div>
          <div className="flex-1 p-2 space-y-1">
            {sidebarLessons.map((item) => {
              const isCurrent = item.id === lessonId;
              const itemCompleted = item.lesson_status === 'COMPLETED';

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavigateLesson(item.id)}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm cursor-pointer ${
                    isCurrent
                      ? 'bg-accent/10 text-accent border border-accent/20'
                      : itemCompleted
                      ? 'text-content-secondary hover:bg-surface-hover'
                      : 'text-content-muted hover:bg-surface-hover'
                  }`}
                >
                  {itemCompleted ? (
                    <span className="material-symbols-outlined text-green-400 text-lg shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                  ) : isCurrent ? (
                    <span className="material-symbols-outlined text-accent text-lg shrink-0">play_circle</span>
                  ) : (
                    <span className="material-symbols-outlined text-content-muted/60 text-lg shrink-0">radio_button_unchecked</span>
                  )}
                  <span className="truncate">{item.title}</span>
                </button>
              );
            })}
          </div>
        </aside>
      </div>

      {/* Bottom Navigation Bar */}
      <div className="border-t border-theme-border bg-surface-card px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {lesson.navigation.previous ? (
            <Link
              to={`/trilhas/${trailId}/aula/${lesson.navigation.previous.id}`}
              className="flex items-center gap-2 text-content-muted hover:text-content-primary transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="text-sm font-medium hidden sm:inline">{lesson.navigation.previous.title}</span>
              <span className="text-sm font-medium sm:hidden">Anterior</span>
            </Link>
          ) : (
            <div />
          )}

          {isCompleted ? (
            lesson.navigation.next ? (
              <Link
                to={`/trilhas/${trailId}/aula/${lesson.navigation.next.id}`}
                className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors"
              >
                <span className="text-sm">Próxima</span>
                <span className="material-symbols-outlined">arrow_forward</span>
              </Link>
            ) : (
              <Link
                to={`/trilhas/${trailId}`}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-xl font-medium transition-colors"
              >
                <span className="material-symbols-outlined">check</span>
                <span className="text-sm">Curso Concluído</span>
              </Link>
            )
          ) : isQuizPending ? (
            <button
              onClick={() => navigate(`/trilhas/${trailId}/aula/${lessonId}/quiz`)}
              className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors"
            >
              <span className="material-symbols-outlined">quiz</span>
              <span className="text-sm">Responder Quiz</span>
            </button>
          ) : (
            <button
              onClick={handleAdvance}
              disabled={!canAdvance || isCompleting}
              className="flex items-center gap-2 bg-accent hover:bg-accent-hover text-accent-contrast px-6 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="complete-button"
            >
              {isCompleting ? (
                <span className="text-sm">Salvando...</span>
              ) : lesson.navigation.next === null ? (
                <>
                  <span className="material-symbols-outlined">check</span>
                  <span className="text-sm">Concluir Curso</span>
                </>
              ) : (
                <>
                  <span className="text-sm">Próxima</span>
                  <span className="material-symbols-outlined">arrow_forward</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseLessonPage;
